import { ref } from 'vue'
import { sendGraphQLQuery } from '@/services/gql-util'
import { TransferProfile, TransferQueueItem, TransferStats } from '@/types/Transfers'

export function useTransfers() {
	const transferProfiles = ref<TransferProfile[]>([])
	const transferQueue = ref<TransferQueueItem[]>([])
	const transferStats = ref<TransferStats>({
		totalTransfers: 0,
		completedToday: 0,
		pendingCount: 0,
		failedCount: 0,
	})
	const transferSummary = ref<Record<string, any>>({})
	const loading = ref(false)
	const error = ref<string | null>(null)

	const getTransferProfiles = async (vhost: string): Promise<void> => {
		loading.value = true
		error.value = null

		try {
			const query = `
			query ($vhost: Int!) {
				Transfers {
					transferlist(vhost: $vhost) {
						id vhost protocol name url port username directory rfilename
						filetype email active notify filenametemplate
					}
				}
			}`
			const data = await sendGraphQLQuery<{
				Transfers: { transferlist: TransferProfile[] }
			}>(query, { vhost: Number(vhost) })
			transferProfiles.value = data?.Transfers?.transferlist || []
		} catch (err: any) {
			console.error('Error fetching transfer profiles:', err)
			error.value = err.message || 'Failed to fetch transfer profiles'
		} finally {
			loading.value = false
		}
	}

	const getTransferQueue = async (vhost: string, status?: string): Promise<void> => {
		loading.value = true
		error.value = null

		try {
			const query = `
			query ($vhost: Int!, $status: String, $limit: Int) {
				Transfers {
					filetransfers(vhost: $vhost, status: $status, limit: $limit) {
						id tid mid time status attempts notified conversionid vhost uid cause publish_at queued_at
					}
				}
			}`
			const data = await sendGraphQLQuery<{
				Transfers: { filetransfers: any[] }
			}>(query, {
				vhost: Number(vhost),
				status: status || null,
				limit: 200,
			})
			const rawData = data?.Transfers?.filetransfers || []
			transferQueue.value = rawData.map((item: any) => ({
				id: item.id,
				mediaId: item.mid,
				mediaTitle: `Media ${item.mid}`,
				thumbUrl: '',
				transferProfileId: item.tid,
				transferProfileName: '',
				status: item.status,
				createdDate: item.queued_at || item.time || '',
				completedDate: '',
				errorMessage: item.cause || '',
				attempts: item.attempts || 0,
				publishAt: item.publish_at || '',
			}))

			// Derive stats from queue data
			const all = transferQueue.value
			const today = new Date().toISOString().split('T')[0]
			transferStats.value = {
				totalTransfers: all.length,
				completedToday: all.filter(
					(i) => i.status === 'success' && i.completedDate?.startsWith(today),
				).length,
				pendingCount: all.filter((i) => i.status === 'queued' || i.status === 'assigned')
					.length,
				failedCount: all.filter((i) => i.status === 'failure').length,
			}
		} catch (err: any) {
			console.error('Error fetching transfer queue:', err)
			error.value = err.message || 'Failed to fetch transfer queue'
		} finally {
			loading.value = false
		}
	}

	const getTransferSummary = async (
		vhost: string,
		startDate?: string,
		endDate?: string,
	): Promise<void> => {
		try {
			const query = `
			query ($vhost: Int!, $startDate: String, $endDate: String) {
				Transfers {
					transfersummary(vhost: $vhost, startDate: $startDate, endDate: $endDate) {
						id tid mid time status attempts
					}
				}
			}`
			const data = await sendGraphQLQuery<{
				Transfers: { transfersummary: any[] }
			}>(query, {
				vhost: Number(vhost),
				startDate: startDate || null,
				endDate: endDate || null,
			})
			transferSummary.value = data?.Transfers?.transfersummary || {}
		} catch (err: any) {
			console.error('Error fetching transfer summary:', err)
		}
	}

	const getTransferStats = async (vhost: string): Promise<void> => {
		// Stats are derived from queue data, so just call getTransferQueue
		await getTransferQueue(vhost)
	}

	const startTransfer = async (
		mediaIds: number[],
		transferProfileId: number,
		vhost?: number,
	): Promise<boolean> => {
		try {
			const query = `
			mutation ($vhost: Int!, $mediaIds: [Int!]!, $transferProfileId: Int!) {
				TransferMutations {
					startTransfers(vhost: $vhost, mediaIds: $mediaIds, transferProfileId: $transferProfileId)
				}
			}`
			const data = await sendGraphQLQuery<{
				TransferMutations: { startTransfers: number }
			}>(query, {
				vhost: vhost || 0,
				mediaIds,
				transferProfileId,
			})
			return (data?.TransferMutations?.startTransfers ?? 0) > 0
		} catch (err) {
			console.error('Error starting transfer:', err)
			return false
		}
	}

	const retryTransfer = async (transferId: number): Promise<boolean> => {
		try {
			const query = `
			mutation ($jobId: Int!) {
				TransferMutations {
					requeueJob(jobId: $jobId)
				}
			}`
			const data = await sendGraphQLQuery<{
				TransferMutations: { requeueJob: boolean }
			}>(query, { jobId: transferId })
			return data?.TransferMutations?.requeueJob ?? false
		} catch (err) {
			console.error('Error retrying transfer:', err)
			return false
		}
	}

	const deleteTransferJob = async (_vhost: string, jobId: number): Promise<boolean> => {
		try {
			const query = `
			mutation ($jobId: Int!) {
				TransferMutations {
					deleteJob(jobId: $jobId)
				}
			}`
			const data = await sendGraphQLQuery<{
				TransferMutations: { deleteJob: boolean }
			}>(query, { jobId })
			return data?.TransferMutations?.deleteJob ?? false
		} catch (err) {
			console.error('Error deleting transfer job:', err)
			return false
		}
	}

	return {
		transferProfiles,
		transferQueue,
		transferStats,
		transferSummary,
		loading,
		error,
		getTransferProfiles,
		getTransferQueue,
		getTransferStats,
		getTransferSummary,
		startTransfer,
		retryTransfer,
		deleteTransferJob,
	}
}
